import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader, EmptyState, PriorityBadge, StageBadge } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Briefcase, PlusCircle, Sparkles, Send, CheckCircle2, XCircle, RotateCcw, Trash2, Loader2, Star } from 'lucide-react';
import { fmtDate, fmtINR } from '@/lib/format';

function ManualRatingInput({ candidate, onSaved }) {
  const [v, setV] = useState(candidate.manual_rating ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (v === '' || v === null) return;
    const num = +v;
    if (isNaN(num) || num < 0 || num > 100) return toast.error('0-100 only');
    setSaving(true);
    try { await api.patch(`/candidates/${candidate.id}`, { manual_rating: num }); toast.success('Manual rating saved'); onSaved && onSaved(); }
    catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-1">
      <Input type="number" min="0" max="100" value={v} onChange={(e) => setV(e.target.value)} className="h-8 w-16 text-right tabular-nums" data-testid={`manual-rating-${candidate.id}`} />
      <Button size="sm" variant="ghost" onClick={save} disabled={saving} data-testid={`manual-rating-save-${candidate.id}`}>Save</Button>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: 'bg-muted text-muted-foreground',
    pending_approval: 'bg-[hsl(43_74%_94%)] text-[hsl(30_90%_30%)]',
    approved: 'bg-[hsl(148_68%_92%)] text-[hsl(148_82%_22%)]',
    closed: 'bg-[hsl(4_90%_95%)] text-[hsl(4_74%_36%)]',
  };
  const labels = { draft: 'Draft', pending_approval: 'Pending approval', approved: 'Approved (Open)', closed: 'Closed' };
  return <Badge className={`${map[status] || map.draft} border-transparent`}>{labels[status] || status}</Badge>;
}

function JobDialog({ companyId, onSaved, existing }) {
  const [open, setOpen] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [f, setF] = useState({
    title: '', positions_count: 1, department: '', location: '', employment_type: 'Full-time',
    experience_min: '', experience_max: '', ctc_min: '', ctc_max: '', skills: [], skills_str: '', description: '',
    priority: 'p1',
  });

  useEffect(() => {
    if (existing) setF({ ...existing, skills_str: (existing.skills || []).join(', '), experience_min: existing.experience_min ?? '', experience_max: existing.experience_max ?? '', ctc_min: existing.ctc_min ?? '', ctc_max: existing.ctc_max ?? '' });
  }, [existing?.id]);

  const parseSkills = (str) => str.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);

  const aiDraft = async () => {
    setDrafting(true);
    try {
      const r = await api.post('/jobs/draft-description', { company_id: companyId, title: f.title, department: f.department, skills: parseSkills(f.skills_str), experience_min: f.experience_min || undefined, experience_max: f.experience_max || undefined });
      setF({ ...f, description: r.data.description });
      toast.success('AI draft ready');
    } catch (e) { toast.error('AI failed'); }
    finally { setDrafting(false); }
  };

  const submit = async () => {
    if (!f.title) return toast.error('Title required');
    const payload = {
      company_id: companyId, title: f.title, positions_count: +(f.positions_count || 1),
      department: f.department || null, location: f.location || null, employment_type: f.employment_type,
      experience_min: f.experience_min ? +f.experience_min : null, experience_max: f.experience_max ? +f.experience_max : null,
      ctc_min: f.ctc_min ? +f.ctc_min : null, ctc_max: f.ctc_max ? +f.ctc_max : null,
      skills: parseSkills(f.skills_str), description: f.description, priority: f.priority,
    };
    try {
      if (existing) await api.patch(`/jobs/${existing.id}`, payload);
      else await api.post('/jobs', payload);
      toast.success('Saved'); setOpen(false); onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{existing ? <Button size="sm" variant="outline" data-testid={`edit-job-${existing.id}`}>Edit</Button> : <Button data-testid="add-job-button"><PlusCircle className="h-4 w-4 mr-2" />New job requirement</Button>}</DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>{existing ? 'Edit job requirement' : 'New job requirement'}</DialogTitle><DialogDescription>Draft the requirement, send for company approval, then attach candidates.</DialogDescription></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-1.5 md:col-span-2"><Label>Role title *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="job-title-input" /></div>
          <div className="space-y-1.5"><Label># positions *</Label><Input type="number" min="1" value={f.positions_count} onChange={(e) => setF({ ...f, positions_count: e.target.value })} data-testid="job-positions-count" /></div>
          <div className="space-y-1.5"><Label>Department</Label><Input value={f.department || ''} onChange={(e) => setF({ ...f, department: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Location</Label><Input value={f.location || ''} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Employment type</Label><Select value={f.employment_type} onValueChange={(v) => setF({ ...f, employment_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Full-time">Full-time</SelectItem><SelectItem value="Part-time">Part-time</SelectItem><SelectItem value="Contract">Contract</SelectItem><SelectItem value="Internship">Internship</SelectItem></SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Experience min (yrs)</Label><Input type="number" value={f.experience_min} onChange={(e) => setF({ ...f, experience_min: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Experience max (yrs)</Label><Input type="number" value={f.experience_max} onChange={(e) => setF({ ...f, experience_max: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>CTC min (₹)</Label><Input type="number" value={f.ctc_min} onChange={(e) => setF({ ...f, ctc_min: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>CTC max (₹)</Label><Input type="number" value={f.ctc_max} onChange={(e) => setF({ ...f, ctc_max: e.target.value })} /></div>
          <div className="space-y-1.5 md:col-span-2"><Label>Priority</Label><Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="p0">P0 High</SelectItem><SelectItem value="p1">P1 Medium</SelectItem><SelectItem value="p2">P2 Low</SelectItem></SelectContent></Select></div>
          <div className="space-y-1.5 md:col-span-2"><Label>Skills (comma separated)</Label><Input value={f.skills_str} onChange={(e) => setF({ ...f, skills_str: e.target.value })} placeholder="React, TypeScript, Redux" data-testid="job-skills-input" /></div>
          <div className="space-y-1.5 md:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Job description (Markdown)</Label>
              <Button size="sm" variant="outline" onClick={aiDraft} disabled={drafting || !f.title} data-testid="job-ai-draft">{drafting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}AI draft</Button>
            </div>
            <Textarea rows={10} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="font-mono text-sm" data-testid="job-description-textarea" />
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="job-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalDialog({ jobId, company, onSent }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState((company?.directors || []).map((d) => d.email).filter(Boolean).join(', '));
  const [cc, setCc] = useState((company?.default_cc_emails || []).join(', '));
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/jobs/${jobId}/send-for-approval`, {
        to: to.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        cc: cc.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        message: msg,
      });
      const st = r.data.result?.status;
      if (st === 'sent') toast.success('Sent for approval');
      else if (st === 'queued') toast.message('Queued as draft (SMTP not configured)');
      else toast.error('Failed');
      setOpen(false); onSent && onSent();
    } catch (e) { toast.error('Failed'); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`send-approval-${jobId}`}><Send className="h-3.5 w-3.5 mr-1" />Send for approval</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Send for approval</DialogTitle><DialogDescription>Email the full requirement to company directors/managers. Company default CC is auto-added.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>To</Label><Input value={to} onChange={(e) => setTo(e.target.value)} data-testid="approval-to" /></div>
          <div className="space-y-1"><Label>CC</Label><Input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="approval-cc" /></div>
          <div className="space-y-1"><Label>Message</Label><Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Any additional message for the approvers" /></div>
        </div>
        <DialogFooter><Button onClick={send} disabled={sending} data-testid="approval-send-btn">{sending ? 'Sending…' : 'Send'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JobDrawer({ jobId, open, onOpenChange, onChanged, company }) {
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [rating, setRating] = useState({});

  const load = async () => {
    if (!jobId) return;
    const [j, c] = await Promise.all([api.get(`/jobs/${jobId}`), api.get(`/jobs/${jobId}/candidates`)]);
    setJob(j.data); setCandidates(c.data);
  };
  useEffect(() => { if (open) load(); }, [jobId, open]);

  const rate = async (cid) => {
    setRating({ ...rating, [cid]: 'loading' });
    try {
      const r = await api.post('/ai/rate-candidate', { candidate_id: cid, job_id: jobId });
      setRating({ ...rating, [cid]: r.data });
      await load();
      toast.success(`Rated: ${r.data.combined_score}/100`);
    } catch (e) { toast.error('Failed'); setRating({ ...rating, [cid]: null }); }
  };

  if (!job) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">{job.title} <StatusBadge status={job.status} /></SheetTitle>
          <SheetDescription>{job.department || 'General'} · {job.positions_filled || 0}/{job.positions_count} filled · {job.applicants_count || 0} applicants</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <Card className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><p className="text-[10px] uppercase text-muted-foreground">Positions</p><p className="font-medium">{job.positions_count}</p></div>
            <div><p className="text-[10px] uppercase text-muted-foreground">Filled</p><p className="font-medium">{job.positions_filled || 0}</p></div>
            <div><p className="text-[10px] uppercase text-muted-foreground">Experience</p><p className="font-medium">{job.experience_min || 0}-{job.experience_max || 0} yrs</p></div>
            <div><p className="text-[10px] uppercase text-muted-foreground">CTC</p><p className="font-medium">{job.ctc_min ? fmtINR(job.ctc_min) : '—'} – {job.ctc_max ? fmtINR(job.ctc_max) : '—'}</p></div>
          </Card>

          <Card className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><p className="text-[10px] uppercase text-muted-foreground">Created</p><p className="font-medium">{fmtDate(job.created_at)}</p></div>
            <div><p className="text-[10px] uppercase text-muted-foreground">Opened</p><p className="font-medium">{job.opened_at ? fmtDate(job.opened_at) : '—'}</p></div>
            <div><p className="text-[10px] uppercase text-muted-foreground">Closed</p><p className="font-medium">{job.closed_at ? fmtDate(job.closed_at) : '—'}</p></div>
            <div><p className="text-[10px] uppercase text-muted-foreground">Days to close</p><p className="font-medium">{job.days_to_close !== undefined && job.days_to_close !== null ? `${job.days_to_close} days` : '—'}</p></div>
          </Card>

          <Card className="p-4">
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Description</p>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm">{job.description || <span className="text-muted-foreground">No description yet. Edit the job to add one (or use AI).</span>}</div>
          </Card>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-display font-semibold">Applicants · {candidates.length}</p>
              <p className="text-xs text-muted-foreground">Sorted by AI fit score</p>
            </div>
            <div className="rounded-xl border bg-card overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Candidate</TableHead><TableHead>Contact</TableHead><TableHead>Stage</TableHead><TableHead>AI</TableHead><TableHead>Keyword</TableHead><TableHead>Manual (0-100)</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {candidates.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No candidates yet linked to this job.</TableCell></TableRow>}
                  {candidates.map((c) => (
                    <TableRow key={c.id} data-testid={`job-cand-row-${c.id}`}>
                      <TableCell><p className="font-medium">{c.name}</p><p className="text-xs text-muted-foreground">Exp: {c.experience_years ?? '—'} yrs · CTC: {c.expected_ctc ? fmtINR(c.expected_ctc) : '—'}</p></TableCell>
                      <TableCell><p className="text-xs">{c.email}</p><p className="text-xs text-muted-foreground">{c.phone || '—'}</p></TableCell>
                      <TableCell><StageBadge stage={c.stage} /></TableCell>
                      <TableCell>
                        {c.ai_rating ? (
                          <div className="flex items-center gap-1"><Star className="h-3 w-3 text-primary" /><span className="tabular-nums font-medium">{c.ai_rating}</span></div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell><span className="tabular-nums text-sm">{c.keyword_score ?? '—'}</span></TableCell>
                      <TableCell>
                        <ManualRatingInput candidate={c} onSaved={load} />
                      </TableCell>
                      <TableCell><Button size="sm" variant="outline" onClick={() => rate(c.id)} disabled={rating[c.id] === 'loading'} data-testid={`rate-cand-${c.id}`}>{rating[c.id] === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Sparkles className="h-3.5 w-3.5 mr-1" />Re-rate</>}</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {job.approval_history && job.approval_history.length > 0 && (
            <Card className="p-4">
              <p className="font-display font-semibold mb-2">Approval activity</p>
              <ul className="text-xs space-y-1 text-muted-foreground">
                {job.approval_history.slice().reverse().map((h) => (
                  <li key={h.id}>{new Date(h.timestamp).toLocaleString()} · {h.action} · {h.actor_name}{h.status ? ` · ${h.status}` : ''}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function JobsPage() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    const [c, j] = await Promise.all([api.get(`/companies/${companyId}`), api.get(`/jobs?company_id=${companyId}`)]);
    setCompany(c.data); setJobs(j.data);
  };
  useEffect(() => { load(); }, [companyId]);

  const approve = async (id) => { await api.post(`/jobs/${id}/approve`, { note: 'Approved via app' }); toast.success('Marked approved'); load(); };
  const close = async (id) => { if (!window.confirm('Close this position?')) return; await api.post(`/jobs/${id}/close`); toast.success('Closed'); load(); };
  const reopen = async (id) => { await api.post(`/jobs/${id}/reopen`); toast.success('Reopened'); load(); };
  const del = async (id) => { if (!window.confirm('Delete requirement?')) return; await api.delete(`/jobs/${id}`); toast.success('Deleted'); load(); };

  return (
    <>
      <PageHeader
        title={`${company?.name || '…'} · Job Requirements`}
        description="Create job requirements, get them approved via email, and see all candidates who applied with AI-ranked fit scores."
        actions={<JobDialog companyId={companyId} onSaved={load} />}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {jobs.length === 0 ? (
          <EmptyState icon={Briefcase} title="No job requirements yet" description="Create one, draft a JD with AI, and email it to your client for approval." />
        ) : (
          <div className="space-y-3">
            {jobs.map((j) => (
              <Card key={j.id} className="p-5" data-testid={`job-card-${j.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-semibold">{j.title}</h3>
                      <StatusBadge status={j.status} />
                      <PriorityBadge priority={j.priority || 'p1'} />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{j.department || '—'} · {j.location || '—'} · {j.experience_min || 0}–{j.experience_max || 0} yrs · {j.employment_type || '—'}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                      <span>{j.positions_filled || 0}/{j.positions_count} filled</span>
                      <span>{j.applicants_count || 0} applicants</span>
                      {j.ctc_min && <span>CTC {fmtINR(j.ctc_min)}–{fmtINR(j.ctc_max)}</span>}
                      {j.skills?.length > 0 && <span className="truncate">Skills: {j.skills.join(', ')}</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-[11px] text-muted-foreground flex-wrap">
                      <span>Created: {fmtDate(j.created_at)}</span>
                      {j.opened_at && <span>· Opened: {fmtDate(j.opened_at)}</span>}
                      {j.closed_at && <span>· Closed: {fmtDate(j.closed_at)}</span>}
                      {j.days_to_close !== undefined && j.days_to_close !== null && <span className="text-primary font-medium">· Closed in {j.days_to_close} days</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setOpenId(j.id)} data-testid={`open-job-${j.id}`}>View</Button>
                    <JobDialog companyId={companyId} existing={j} onSaved={load} />
                    {(j.status === 'draft' || j.status === 'pending_approval') && company && <ApprovalDialog jobId={j.id} company={company} onSent={load} />}
                    {j.status !== 'approved' && j.status !== 'closed' && <Button size="sm" onClick={() => approve(j.id)} data-testid={`approve-job-${j.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve</Button>}
                    {j.status === 'approved' && <Button size="sm" variant="outline" onClick={() => close(j.id)} data-testid={`close-job-${j.id}`}><XCircle className="h-3.5 w-3.5 mr-1" />Close</Button>}
                    {j.status === 'closed' && <Button size="sm" variant="outline" onClick={() => reopen(j.id)} data-testid={`reopen-job-${j.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Reopen</Button>}
                    <Button size="icon" variant="ghost" onClick={() => del(j.id)} data-testid={`delete-job-${j.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <JobDrawer jobId={openId} open={!!openId} onOpenChange={(o) => !o && setOpenId(null)} onChanged={load} company={company} />
    </>
  );
}
